import { createFileRoute } from "@tanstack/react-router";
import { useState, type ChangeEvent } from "react";
import { ImagePlus, Landmark, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AdminTitle, Field, Status } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { meta } from "@/components/store/page-parts";
import { useStore } from "@/lib/store-context";
import type { Gateway } from "@/data/gateways";
import { currencies } from "@/lib/pricing";

export const Route = createFileRoute("/admin/payments")({
  head: () => meta("Payments — BetterAccess Admin", "Enable payment gateways, upload their icons and store credentials."),
  component: Payments,
});

function Payments() {
  const { gateways, setGateways, support, setSupport } = useStore();
  const [openId, setOpenId] = useState("");
  const [saving, setSaving] = useState(false);

  // Binance settings
  const [binancePayId, setBinancePayId] = useState(support.binance_pay_id || "889923412");
  const [binanceUid, setBinanceUid] = useState(support.binance_pay_uid || "782390114");

  const update = (id: string, patch: Partial<Gateway>) => {
    const updated = gateways.map(g => (g.id === id ? { ...g, ...patch } : g));
    setGateways(updated);
    void persistGateways(updated);
  };

  const persistGateways = async (newList: Gateway[]) => {
    try {
      await fetch("/api/admin/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          store_gateways: JSON.stringify(newList),
          binance_pay_id: binancePayId,
          binance_pay_uid: binanceUid,
        }),
      });
    } catch {
      // ignore
    }
  };

  const upload = (id: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 700_000) {
      toast.error("Choose an icon under 700KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      update(id, { icon: String(reader.result) });
      toast.success("Gateway icon updated");
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCredentials = async () => {
    setSaving(true);
    try {
      await fetch("/api/admin/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          binance_pay_id: binancePayId,
          binance_pay_uid: binanceUid,
        }),
      });
      setSupport({ ...support, binance_pay_id: binancePayId, binance_pay_uid: binanceUid });
      toast.success("Payment settings and credentials saved to database!");
    } catch (err: any) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = gateways.filter(g => g.enabled).length;

  return (
    <>
      <AdminTitle
        eyebrow="Checkout"
        title="Payment Gateways"
        copy="Only enabled gateways appear at checkout. Changes save globally to live database."
      />

      <div className="mb-6 rounded-lg border bg-card p-5">
        <h2 className="text-sm font-black text-foreground">Binance Pay Gateway Credentials</h2>
        <p className="mt-1 text-xs text-muted-foreground">Used for cryptocurrency payment instructions and instant automated verification.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Binance Pay ID">
            <input
              value={binancePayId}
              onChange={e => setBinancePayId(e.target.value)}
              placeholder="e.g. 889923412"
              className="h-10 rounded-md border bg-background px-3 font-mono font-normal"
            />
          </Field>
          <Field label="Binance Account UID">
            <input
              value={binanceUid}
              onChange={e => setBinanceUid(e.target.value)}
              placeholder="e.g. 782390114"
              className="h-10 rounded-md border bg-background px-3 font-mono font-normal"
            />
          </Field>
        </div>
        <Button size="sm" className="mt-4 font-bold" onClick={handleSaveCredentials} disabled={saving}>
          {saving ? <RefreshCw className="size-3.5 animate-spin" /> : "Save Binance Credentials"}
        </Button>
      </div>

      <p className="mb-5 inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-xs font-semibold text-muted-foreground">
        {enabledCount} of {gateways.length} gateways visible to customers
      </p>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {gateways.map(gateway => (
          <article key={gateway.id} className="premium-panel p-5">
            <div className="flex items-start justify-between gap-3">
              <span className="grid size-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-primary/10 text-primary">
                {gateway.icon ? <img src={gateway.icon} alt="" className="size-11 object-contain" /> : <Landmark className="size-5" />}
              </span>
              <div className="flex items-center gap-2">
                <Status>{gateway.mode === "Live" ? "Live" : "Test mode"}</Status>
                <Switch
                  checked={gateway.enabled}
                  onCheckedChange={value => update(gateway.id, { enabled: value })}
                  aria-label={`Show ${gateway.name} at checkout`}
                />
              </div>
            </div>
            <h2 className="mt-4 font-black">{gateway.name}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{gateway.copy}</p>
            <p className="mt-2 text-[11px] font-bold text-primary">Settles in {gateway.settleCurrency}</p>
            <Button variant="outline" className="mt-4 w-full" onClick={() => setOpenId(openId === gateway.id ? "" : gateway.id)}>
              {openId === gateway.id ? "Close" : "Configure"}
            </Button>
            {openId === gateway.id && (
              <div className="mt-4 grid gap-4 border-t pt-4">
                <Field label="Display name">
                  <input
                    value={gateway.name}
                    onChange={e => update(gateway.id, { name: e.target.value })}
                    className="h-10 rounded-md border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
                  />
                </Field>
                <Field label="Customer note">
                  <input
                    value={gateway.copy}
                    onChange={e => update(gateway.id, { copy: e.target.value })}
                    className="h-10 rounded-md border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
                  />
                </Field>
                <Field label="Settlement currency">
                  <select
                    value={gateway.settleCurrency}
                    onChange={e => update(gateway.id, { settleCurrency: e.target.value })}
                    className="h-10 rounded-md border bg-background px-3 font-normal"
                  >
                    {currencies.map(code => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Mode">
                  <select
                    value={gateway.mode}
                    onChange={e => update(gateway.id, { mode: e.target.value as Gateway["mode"] })}
                    className="h-10 rounded-md border bg-background px-3 font-normal"
                  >
                    <option value="Live">Live</option>
                    <option value="Test">Test</option>
                  </select>
                </Field>
                <label className="grid cursor-pointer place-items-center rounded-lg border border-dashed bg-secondary/40 p-4 text-center text-xs font-bold transition hover:border-primary">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="sr-only" onChange={upload(gateway.id)} />
                  <ImagePlus className="size-5 text-primary" />
                  <span className="mt-2">{gateway.icon ? "Replace gateway icon" : "Upload gateway icon"}</span>
                  <small className="font-normal text-muted-foreground">PNG, SVG or WEBP under 700KB</small>
                </label>
              </div>
            )}
          </article>
        ))}
      </div>
    </>
  );
}
